import { useFormik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { IoMdSend } from "react-icons/io";
import InfiniteScroll from "react-infinite-scroll-component";
import { twMerge } from "tailwind-merge";
import * as Yup from "yup";
import { getComplaintRepliess } from "../../apis/help-desks/get-complaint-replies";
import { readHelpdeskChat } from "../../apis/help-desks/read-helpdesk-chat";
import { sendComplaintReply } from "../../apis/help-desks/set-complaint-reply";
import socket from "../../lib/socket-io";
import Button from "../Button";
import Input from "../Input";
import Modal from "../Modal";
import { ComplaintType, reportingMap } from "./ViewDetails";
import { formatMessageDate } from '../../utils';

interface ChatModalProps {
  isVisible: boolean;
  onHide: () => void;
  complaint: ComplaintType | null;
  onMessageRead?: (complaintId: string) => void;
}

type MessageType = {
  complaint: string;
  from: string;
  message: string;
  status: string;
  userType: string;
  _id: string;
  createdAt: string;
};

const MessageBubble = ({
  message,
  date,
  from,
}: {
  message: string;
  from: string;
  date: string;
}) => {
  const isAdmin = from === "admin";

  return (
    <div
      className={twMerge(
        "flex w-full",
        isAdmin ? "justify-end" : "justify-start"
      )}
    >
      <div
        className={twMerge(
          "flex flex-col w-full gap-1",
          isAdmin ? "items-end" : "items-start"
        )}
      >
        <p
          className={twMerge(
            "px-4 py-2 whitespace-normal break-words text-sm",
            isAdmin
              ? "bg-primary text-white rounded-tl-2xl w-[95%] rounded-bl-2xl rounded-br-2xl"
              : "bg-gray-200 text-black rounded-tr-2xl w-[95%] rounded-bl-2xl rounded-br-2xl"
          )}
        >
          {message}
          <div className='flex justify-end'>
            {date && (
              <span className={twMerge("text-gray-400 text-xs px-1", isAdmin ? "text-white" : "")}>
                {formatMessageDate(date)}
              </span>
            )}
          </div>
        </p>

      </div>
    </div>
  );
};

const ChatModal = ({
  isVisible,
  onHide,
  complaint,
  onMessageRead,
}: ChatModalProps) => {
  const [replies, setReplies] = useState<MessageType[]>([]);
  const [isRepliesLoading, setIsRepliesLoading] = useState(false);
  const [repliesCurrentPage, setRepliesCurrentPage] = useState(0);
  const [repliesTotalDocs, setRepliesTotalDocs] = useState(1);
  const currentComplaintId = useRef<string | null>(null);

  const messageForm = useFormik({
    initialValues: { message: "" },
    validationSchema: Yup.object().shape({
      message: Yup.string().trim().required("This field is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      if (!complaint) return;
      const res = await sendComplaintReply({
        complaint: complaint?._id,
        to: complaint[reportingMap[complaint?.userType]]?._id,
        toUserType: complaint?.userType,
        message: values.message,
      });
      if (res?.success) {
        setReplies((prevReplies) => [res?.data?.data, ...prevReplies]);
        resetForm();
      }
    },
  });

  const getComplaintReplies = async () => {
    if (repliesCurrentPage < 1) setIsRepliesLoading(true);
    const res = await getComplaintRepliess(complaint?._id!, {
      sort: "-1",
      page: repliesCurrentPage + 1,
    });
    if (res.success) {
      setReplies((prev) => [...prev, ...res.data?.docs]);
      setRepliesTotalDocs(res.data?.totalDocs);
      setRepliesCurrentPage(res.data?.page);
    }
    setIsRepliesLoading(false);
  };

  const handleMarkAsRead = async () => {
    if (!complaint) return;
    const res = await readHelpdeskChat(complaint?._id);
    if (res?.success && onMessageRead) {
      onMessageRead(complaint?._id);
    }
  };

  useEffect(() => {
    const handleNewMessage = (data: { type: string; data: MessageType }) => {
      console.log("this is socket listner");

      if (data.data?.complaint === currentComplaintId.current) {
        handleMarkAsRead();
        setReplies((prevReplies) => [data.data, ...prevReplies]);
      }
    };
    socket.on("receive_helpdesk_message_success", handleNewMessage);
    return () => {
      socket.off("receive_helpdesk_message_success", handleNewMessage);
    };
  }, [handleMarkAsRead]);

  useEffect(() => {
    currentComplaintId.current = complaint?._id || null;
  }, [complaint?._id]);

  useEffect(() => {
    if (complaint?._id && isVisible) handleMarkAsRead();
  }, [complaint?._id, isVisible]);

  useEffect(() => {
    if (complaint?._id) {
      setReplies([]);
      setRepliesCurrentPage(0);
      setRepliesTotalDocs(1);
      getComplaintReplies();
    }
  }, [complaint?._id]);

  return (
    <Modal
      isVisible={isVisible}
      heading={
        complaint?.[reportingMap[complaint?.userType!]]?.fullName || "N/A"
      }
      onHide={() => {
        setReplies([]);
        setRepliesCurrentPage(0);
        messageForm.resetForm();
        setRepliesTotalDocs(0);
        onHide();
      }}
    >
      {complaint ? (
        <div className="space-y-4 px-4">
          {isRepliesLoading ? (
            <div className="min-h-[400px] w-full bg-white flex items-center justify-center rounded-lg">
              <div className="loader"></div>
            </div>
          ) : (
            <>
              {replies.length > 0 ? (
                <div
                  id="scrollableDiv"
                  style={{
                    height: 300,
                    overflow: "auto",
                    display: "flex",
                    flexDirection: "column-reverse",
                  }}
                >
                  <InfiniteScroll
                    loader="Loading..."
                    next={getComplaintReplies}
                    dataLength={repliesCurrentPage * 10}
                    hasMore={repliesTotalDocs / 10 > repliesCurrentPage}
                    inverse={true}
                    height={300}
                    className="flex px-4 overflow-auto gap-y-2 flex-col-reverse"
                  >
                    {replies?.map((reply) => (
                      <MessageBubble
                        key={reply?._id}
                        date={reply?.createdAt}
                        from={reply?.userType}
                        message={reply?.message}
                      />
                    ))}
                  </InfiniteScroll>
                </div>
              ) : (
                <p className="text-center py-4">No messages</p>
              )}

              <form
                onSubmit={messageForm.handleSubmit}
                className="flex items-center w-full gap-2"
              >
                <Input
                  placeholder="Type a message"
                  containerClassName="w-full"
                  name="message"
                  onChange={messageForm.handleChange}
                  disabled={messageForm.isSubmitting}
                  value={messageForm.values.message}
                />
                <Button
                  isLoading={messageForm.isSubmitting}
                  type="submit"
                  className="flex-shrink-0 w-14 h-14 rounded-full bg-primary flex items-center justify-center"
                >
                  {!messageForm?.isSubmitting && (
                    <IoMdSend className="text-white" />
                  )}
                </Button>
              </form>
            </>
          )}
        </div>
      ) : null}
    </Modal>
  );
};

export default ChatModal;